import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/* ======================
   API
====================== */
export type ToastVariant = "default" | "destructive" | "success" | "info";

export type ToastOptions = {
  title?: string;
  description?: string;
  variant?: ToastVariant;
  /** ms (default 3000, min 1200) */
  duration?: number;
};

type ToastItem = {
  id: string;
  title?: string;
  description?: string;
  variant: ToastVariant;
  duration: number;
};

type ToastContextValue = {
  addToast: (opts: ToastOptions) => void;
  removeToast: (id: string) => void;
  toasts: ReadonlyArray<ToastItem>;
};

const ToastContext = createContext<ToastContextValue | null>(null);

/** ---------- Global dispatcher so `import { toast }` works ---------- */
let globalAddToast: ((opts: ToastOptions) => void) | null = null;
const pendingQueue: ToastOptions[] = [];

/** Named export: `import { toast } from "@/components/ui/use-toast"` */
export function toast(opts: ToastOptions) {
  if (globalAddToast) {
    globalAddToast(opts);
  } else {
    pendingQueue.push(opts);
    if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") {
      console.warn("[use-toast] toast() called before <ToastProvider> mounted; queuing.");
    }
  }
}
/** ------------------------------------------------------------------- */

export function ToastProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef<Map<string, number>>(new Map());

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer != null) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const addToast = useCallback(
    (opts: ToastOptions) => {
      // --- HARD GUARD: prevent "empty" toasts (blank UI) ---
      const hasTitle = typeof opts.title === "string" && opts.title.trim().length > 0;
      const hasDesc = typeof opts.description === "string" && opts.description.trim().length > 0;
      if (!hasTitle && !hasDesc) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("[use-toast] Ignored toast with no title/description.", opts);
        }
        return;
      }

      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const item: ToastItem = {
        id,
        title: hasTitle ? opts.title!.trim() : undefined,
        description: hasDesc ? opts.description!.trim() : undefined,
        variant: opts.variant ?? "default",
        duration: Math.max(1200, opts.duration ?? 3000),
      };
      setToasts((prev) => [...prev, item]);

      const timeoutId = window.setTimeout(() => removeToast(id), item.duration);
      timers.current.set(id, timeoutId);
    },
    [removeToast],
  );

  // Wire global dispatcher + flush queued toasts
  useEffect(() => {
    globalAddToast = addToast;
    if (pendingQueue.length) {
      const items = pendingQueue.splice(0);
      items.forEach((opts) => addToast(opts));
    }
    return () => {
      globalAddToast = null;
    };
  }, [addToast]);

  const value = useMemo<ToastContextValue>(() => ({ addToast, removeToast, toasts }), [addToast, removeToast, toasts]);

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

/** Hook: const { toast } = useToast(); toast({ title: "...", description: "..." }) */
export function useToast(): { toast: (opts: ToastOptions) => void } {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a <ToastProvider>");
  return { toast: ctx.addToast };
}

/* ======================
   Renderer
====================== */
export function Toaster({
  position = "bottom-right",
}: {
  position?: "top-left" | "top-right" | "top-center" | "bottom-left" | "bottom-right" | "bottom-center";
}): JSX.Element | null {
  const ctx = useContext(ToastContext);
  if (!ctx) return null;

  const posClass =
    position === "top-left"
      ? "top-4 left-4"
      : position === "top-right"
      ? "top-4 right-4"
      : position === "top-center"
      ? "top-4 left-1/2 -translate-x-1/2"
      : position === "bottom-left"
      ? "bottom-4 left-4"
      : position === "bottom-right"
      ? "bottom-20 sm:bottom-4 right-4" // clear bottom nav on mobile
      : "bottom-20 sm:bottom-4 left-1/2 -translate-x-1/2";

  return (
    <div className={`fixed z-50 ${posClass} space-y-2 w-[min(92vw,380px)]`}>
      {ctx.toasts.map((t) => (
        <ToastCard key={t.id} item={t} onClose={() => ctx.removeToast(t.id)} />
      ))}
    </div>
  );
}

/* ======================
   Card
====================== */
function ToastCard({ item, onClose }: { item: ToastItem; onClose: () => void }): JSX.Element {
  const theme = getTheme(item.variant);

  return (
    <div
      role="status"
      className={[
        "rounded-2xl border shadow-lg p-3 transition",
        "backdrop-blur-md", // subtle glass
        theme.bg,
        theme.border,
        theme.text,
      ].join(" ")}
    >
      <div className="flex items-start gap-3">
        {/* Mascot icon */}
        <div className="mt-0.5 shrink-0">
          <MascotIcon variant={item.variant} />
        </div>

        <div className="min-w-0 flex-1">
          {item.title && <div className="font-semibold tracking-wide">{item.title}</div>}
          {item.description && <div className="text-sm/5 opacity-95">{item.description}</div>}
        </div>

        <button
          aria-label="Close"
          onClick={onClose}
          className={[
            "rounded p-1 transition",
            "hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/30",
            theme.text,
          ].join(" ")}
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
            <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.3 19.71 2.89 18.3 9.18 12 2.89 5.71 4.3 4.29l6.29 6.3 6.29-6.3z" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/* ======================
   Theme (Majestic purple + gold)
====================== */
function getTheme(variant: ToastVariant): {
  bg: string;
  border: string;
  text: string;
} {
  // Single aesthetic across variants per your request
  // Purple background (violet-900-ish), gold text (yellow-300)
  return {
    bg: "bg-violet-900/95",
    border: "border-yellow-400/30",
    text: "text-yellow-300",
  };
}

/* ======================
   Mascot Icon (“Bob”) — exported so header can reuse
   - success/info/default: excited jump (animate-bounce)
   - destructive: sad pose (no bounce, slight tilt)
   Accepts optional className and bounce overrides.
====================== */
export function MascotIcon({
  variant,
  className,
  bounce,
}: {
  variant: ToastVariant;
  className?: string;
  bounce?: boolean;
}) {
  const isHappy = variant !== "destructive";
  const shouldBounce = bounce ?? isHappy;
  const cn = [
    "h-6 w-6",
    shouldBounce ? "animate-bounce" : isHappy ? "" : "-rotate-6 opacity-95",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  // High-contrast palette
  const skin = "#A3E635";     // lime-400
  const outline = "#0B0F1A";  // near-black
  const eye = "#FDE047";      // yellow-300
  const pupil = "#EF4444";    // red-500
  const cloak = isHappy ? "#F97316" : "#DC2626"; // orange or red
  const belt = "#78350F";     // brown
  const boot = "#1F2937";     // slate-800
  const highlight = "#FFFFFF"; // white pop

  return (
    <svg
      viewBox="0 0 16 16"
      shapeRendering="crispEdges"
      preserveAspectRatio="xMidYMid meet"
      className={cn}
      style={{ filter: "drop-shadow(0 1px 0 rgba(0,0,0,.85)) drop-shadow(0 0 6px rgba(0,0,0,.4))" }}
      aria-hidden="true"
    >
      {/* Outline (head + ears) */}
      <rect x="3" y="2" width="10" height="1" fill={outline} />
      <rect x="2" y="3" width="1" height="3" fill={outline} />
      <rect x="13" y="3" width="1" height="3" fill={outline} />
      <rect x="3" y="7" width="10" height="1" fill={outline} />

      {/* Ears tips + fill */}
      <rect x="1" y="4" width="1" height="1" fill={outline} />
      <rect x="14" y="4" width="1" height="1" fill={outline} />
      <rect x="2" y="4" width="1" height="1" fill={skin} />
      <rect x="13" y="4" width="1" height="1" fill={skin} />

      {/* Head fill */}
      <rect x="4" y="3" width="8" height="4" fill={skin} />

      {/* Eyes + pupils */}
      <rect x="5" y="4" width="2" height="1" fill={eye} />
      <rect x="9" y="4" width="2" height="1" fill={eye} />
      <rect x="6" y="4" width="1" height="1" fill={pupil} />
      <rect x="10" y="4" width="1" height="1" fill={pupil} />

      {/* Nose + mouth */}
      <rect x="8" y="5" width="1" height="1" fill={skin} />
      <rect x="6" y="6" width="4" height="1" fill={outline} />

      {/* Cloak/body outline */}
      <rect x="3" y="8" width="10" height="1" fill={outline} />
      <rect x="3" y="8" width="1" height="4" fill={outline} />
      <rect x="12" y="8" width="1" height="4" fill={outline} />
      <rect x="4" y="12" width="8" height="1" fill={outline} />

      {/* Cloak fill + belt */}
      <rect x="4" y="9" width="8" height="3" fill={cloak} />
      <rect x="4" y="11" width="8" height="1" fill={belt} />

      {/* Arms + hands */}
      <rect x="3" y="9" width="1" height="1" fill={cloak} />
      <rect x="12" y="9" width="1" height="1" fill={cloak} />
      <rect x="2" y="9" width="1" height="1" fill={skin} />
      <rect x="13" y="9" width="1" height="1" fill={skin} />

      {/* Legs + boots */}
      <rect x="5" y="13" width="2" height="1" fill={outline} />
      <rect x="9" y="13" width="2" height="1" fill={outline} />
      <rect x="5" y="14" width="2" height="1" fill={boot} />
      <rect x="9" y="14" width="2" height="1" fill={boot} />

      {/* Highlights for extra contrast */}
      <rect x="4" y="3" width="1" height="1" fill={highlight} opacity="0.6" />
      <rect x="4" y="9" width="1" height="1" fill={highlight} opacity="0.35" />
    </svg>
  );
}
